/**
 * CTRL_14 Configuration Panel - Data Minimization
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Collapse, Button, Alert, Spin, Select, InputNumber, Form } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

const MINIMIZATION_CHECKS = [
  { value: 'data_minimized', label: 'Data Minimized' },
  { value: 'unnecessary_features_removed', label: 'Unnecessary Features Removed' },
  { value: 'retention_policy_applied', label: 'Retention Policy Applied' },
  { value: 'features_relevant', label: 'Features Relevant to Purpose' },
  { value: 'privacy_preserving', label: 'Privacy Preserving' }
];

const FEATURE_CATEGORIES = [
  'demographic_sensitive',
  'behavioral',
  'geolocation',
  'biometric',
  'financial',
  'health',
  'personal_identifiers'
];

const MINIMIZATION_STRATEGIES = [
  { value: 'manual', label: 'Manual Review' },
  { value: 'automatic', label: 'Automatic Removal' },
  { value: 'hybrid', label: 'Hybrid (Auto + Manual Review)' }
];

function CTRL_14_ConfigPanel({ project, projectId, controlCode }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [configData, setConfigData] = useState({
    enabled: false,
    enforce_minimization: true,
    required_checks: ['data_minimized', 'unnecessary_features_removed'],
    max_retention_days: 365,
    auto_remove_categories: ['demographic_sensitive', 'behavioral'],
    auto_remove_flagged_features: false,
    minimization_strategy: 'hybrid'
  });

  useEffect(() => {
    if (projectId) {
      fetchConfig();
    }
  }, [projectId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getControlConfig(projectId, controlCode);
      if (res.data) {
        setConfig(res.data);
        setConfigData({
          enabled: res.data.enabled || false,
          ...res.data.config
        });
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        control_code: controlCode,
        enabled: configData.enabled,
        config: {
          enforce_minimization: configData.enforce_minimization,
          required_checks: configData.required_checks,
          max_retention_days: configData.max_retention_days,
          auto_remove_categories: configData.auto_remove_categories,
          auto_remove_flagged_features: configData.auto_remove_flagged_features,
          minimization_strategy: configData.minimization_strategy
        }
      };

      await saveControlConfig(projectId, controlCode, payload);
      showSuccess('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <h3>CTRL_14: Data Minimization Configuration</h3>
        <p style={{ color: '#666' }}>
          Configure data minimization rules to ensure only necessary data is collected and retained
        </p>
      </div>

      <Collapse defaultActiveKey={configData.enabled ? ['1', '2'] : ['1']}>
        <Panel header="1. Enable Data Minimization Control" key="1">
          <Switch
            checked={configData.enabled}
            onChange={(checked) => updateConfig('enabled', checked)}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <p style={{ marginTop: '12px', color: '#666' }}>
            When enabled, events will be checked for data minimization compliance
          </p>
        </Panel>

        {configData.enabled && (
          <Panel header="2. Minimization Configuration" key="2">
            <Form layout="vertical">
              <Form.Item label="Enforcement Settings">
                <Switch
                  checked={configData.enforce_minimization}
                  onChange={(checked) => updateConfig('enforce_minimization', checked)}
                  checkedChildren="Enforced"
                  unCheckedChildren="Advisory"
                  style={{ marginRight: '12px' }}
                />
                <span>Enforce data minimization rules (block non-compliant events)</span>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  When enforced, events failing minimization checks will be rejected
                </p>
              </Form.Item>

              <Form.Item label="Required Minimization Checks">
                <Select
                  mode="multiple"
                  value={configData.required_checks || []}
                  onChange={(value) => updateConfig('required_checks', value)}
                  style={{ width: '100%' }}
                  placeholder="Select required checks"
                >
                  {MINIMIZATION_CHECKS.map(check => (
                    <Option key={check.value} value={check.value}>
                      {check.label}
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Checks that must pass for event to be compliant
                </p>
              </Form.Item>

              <Form.Item label="Maximum Retention Period">
                <InputNumber
                  min={1}
                  max={3650}
                  value={configData.max_retention_days}
                  onChange={(value) => updateConfig('max_retention_days', value)}
                  style={{ width: '200px' }}
                  addonAfter="days"
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Maximum number of days data can be retained (default: 365)
                </p>
              </Form.Item>

              <Form.Item label="Auto-Remove Feature Categories">
                <Select
                  mode="tags"
                  value={configData.auto_remove_categories || []}
                  onChange={(value) => updateConfig('auto_remove_categories', value)}
                  style={{ width: '100%' }}
                  placeholder="Select or enter feature categories to remove"
                >
                  {FEATURE_CATEGORIES.map(category => (
                    <Option key={category} value={category}>
                      {category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Feature categories that should be automatically removed (e.g., demographic_sensitive, behavioral)
                </p>
              </Form.Item>

              <Form.Item label="Minimization Strategy">
                <Select
                  value={configData.minimization_strategy}
                  onChange={(value) => updateConfig('minimization_strategy', value)}
                  style={{ width: '100%' }}
                >
                  {MINIMIZATION_STRATEGIES.map(strategy => (
                    <Option key={strategy.value} value={strategy.value}>
                      {strategy.label}
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  How data minimization should be applied
                </p>
              </Form.Item>

              <Form.Item label="Automatic Removal">
                <Switch
                  checked={configData.auto_remove_flagged_features}
                  onChange={(checked) => updateConfig('auto_remove_flagged_features', checked)}
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                  style={{ marginRight: '12px' }}
                />
                <span>Automatically remove flagged features from events</span>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  ⚠️ When enabled, features matching auto-remove categories will be stripped from events
                </p>
              </Form.Item>
            </Form>
          </Panel>
        )}
      </Collapse>

      {configData.enabled && (
        <Alert
          message="Configuration Summary"
          description={
            <div>
              <div><strong>Enforcement:</strong> {configData.enforce_minimization ? 'Enforced' : 'Advisory Only'}</div>
              <div><strong>Required Checks:</strong> {configData.required_checks?.join(', ') || 'None'}</div>
              <div><strong>Max Retention:</strong> {configData.max_retention_days} days</div>
              <div><strong>Auto-Remove Categories:</strong> {configData.auto_remove_categories?.join(', ') || 'None'}</div>
              <div><strong>Strategy:</strong> {MINIMIZATION_STRATEGIES.find(s => s.value === configData.minimization_strategy)?.label || 'Not Set'}</div>
              <div><strong>Auto-Remove Features:</strong> {configData.auto_remove_flagged_features ? 'Yes' : 'No'}</div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: '16px' }}
        />
      )}

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
        style={{ marginTop: '16px' }}
        size="large"
      >
        Save Configuration
      </Button>
    </Card>
  );
}

export default CTRL_14_ConfigPanel;
